import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, MenuItem, Paper, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import InvitationArtwork from '../components/InvitationArtwork';
import { readInvitation, respondToInvitation, planningError } from '../actions/planning';

export default function Invitation() {
  const location = useLocation();
  const token = new URLSearchParams(location.hash.slice(1)).get('token') || '';
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [rsvp, setRsvp] = useState('');
  const [plusOne, setPlusOne] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex, nofollow';
    document.head.appendChild(robots);
    return () => { meta.remove(); robots.remove(); };
  }, []);
  useEffect(() => {
    let active = true;
    setData(null);
    setError('');
    setSaved(false);
    readInvitation(token).then(result => {
      if (!active) return;
      setData(result);
      setRsvp(['Attending', 'Not Attending'].includes(result.rsvp) ? result.rsvp : '');
      setPlusOne(result.plus_one);
    }).catch(reason => { if (active) setError(planningError(reason)); });
    return () => { active = false; };
  }, [token, retry]);
  useEffect(() => {
    if (!data) return;
    let timer;
    const checkDeadline = () => {
      const remaining = new Date(data.expires_at).getTime() - Date.now();
      if (remaining <= 0) {
        setData(null);
        setError('This invitation is unavailable or has expired. Please contact your host.');
      } else {
        timer = window.setTimeout(checkDeadline, Math.min(remaining, 2147483647));
      }
    };
    checkDeadline();
    return () => window.clearTimeout(timer);
  }, [data]);
  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await respondToInvitation(token, { rsvp, plus_one: rsvp === 'Attending' ? plusOne : 0 });
      setSaved(true);
      setData(current => current ? { ...current, responded: true } : current);
    } catch (reason) {
      if (reason.response?.status === 404) setData(null);
      setError(planningError(reason));
    }
    finally { setSaving(false); }
  };
  return (
    <Box sx={{ maxWidth: 820, mx: 'auto', py: { xs: 1, sm: 4 } }}>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 2, letterSpacing: '.18em' }}>Event Planner / Invitations</Typography>
      {!data && !error && <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress aria-label="Loading invitation" /></Box>}
      {!data && error && <Alert severity="error" action={<Button onClick={() => setRetry(value => value + 1)}>Retry</Button>}>{error}</Alert>}
      {data && <Paper elevation={0} sx={{ overflow: 'hidden', borderRadius: { xs: 4, sm: 6 }, border: 1, borderColor: 'divider', boxShadow: theme => theme.palette.mode === 'dark' ? '0 24px 80px #00000055' : '0 24px 80px #183a6220' }}>
        <InvitationArtwork title={data.title} date={data.date} artwork={data.artwork} />
        <Box sx={{ p: { xs: 3, sm: 5 } }}>
          <Typography variant="h5" fontWeight={700}>A little celebration. A place for you.</Typography>
          <Typography color="text.secondary" sx={{ mt: 1, overflowWrap: 'anywhere' }}>Reserved for {data.guest_name}{data.max_plus_ones > 0 ? ` and up to ${data.max_plus_ones} additional guest${data.max_plus_ones === 1 ? '' : 's'}` : ''}.</Typography>
          {data.location && <Box sx={{ my: 3 }}><Typography variant="overline" color="text.secondary">Where we will gather</Typography><Typography sx={{ overflowWrap: 'anywhere' }}>{data.location}</Typography></Box>}
          {data.message && <Typography sx={{ my: 3, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.8 }}>{data.message}</Typography>}
          <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight={650} sx={{ mb: 2 }}>Will you join us?</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>RSVP by {new Date(data.expires_at).toLocaleString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })} ({Intl.DateTimeFormat().resolvedOptions().timeZone}). This link closes after the deadline.</Typography>
            <ToggleButtonGroup exclusive value={rsvp} onChange={(_e, value) => { if (value) { setRsvp(value); setSaved(false); } }} fullWidth aria-label="Attendance" disabled={saving}>
              <ToggleButton value="Attending" sx={{ borderRadius: 3, textTransform: 'none', py: 1.5 }}>I will be there</ToggleButton>
              <ToggleButton value="Not Attending" sx={{ borderRadius: 3, textTransform: 'none', py: 1.5 }}>Cannot make it</ToggleButton>
            </ToggleButtonGroup>
            {rsvp === 'Attending' && data.max_plus_ones > 0 && <TextField select fullWidth label="Additional guests" value={plusOne} onChange={event => { setPlusOne(Number(event.target.value)); setSaved(false); }} disabled={saving} sx={{ mt: 3 }}>
              {Array.from({ length: data.max_plus_ones + 1 }, (_, n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
            </TextField>}
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            {saved && <Alert severity="success" role="status" sx={{ mt: 2 }}>Your response is saved. You can return to this private link to update it until the RSVP deadline.</Alert>}
            <Button fullWidth size="large" variant="contained" onClick={save} disabled={!rsvp || saving || saved} sx={{ mt: 3, py: 1.6, borderRadius: 3, textTransform: 'none', fontWeight: 650 }}>{saving ? 'Saving your response...' : saved ? 'Response saved' : data.responded ? 'Update my response' : 'Send my response'}</Button>
            <Stack spacing={0.5} sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Your response is shared with the event organizers.</Typography>
              <Typography variant="caption" color="text.secondary">This link is personal to your invitation. Please do not forward it.</Typography>
            </Stack>
          </Box>
        </Box>
      </Paper>}
    </Box>
  );
}
